import { styled } from "@mui/material/styles";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridSelectionModel,
  type GridCallbackDetails,
} from "@mui/x-data-grid-pro";
import { useApiClient } from "@/hooks/useApiClient";
import { Stack, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  forwardRef,
  type ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
} from "react";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  type ToolbarProps,
  CommercialMonitoringDataGridToolbar,
} from "./CommercialMonitoringDataGridToolbar";
import type {
  GetCommercialMonitoringSummariesAPIResponse,
  CommercialMonitoringSummaryRow,
  CommercialMonitoringFeedGridProps,
  DisplayMonthlyUpdatable,
} from "@/features/commercialRegistrationMonitoring/types";
import { getDisplayReceptionReason } from "@/features/commercialRegistrationMonitoring/utils/getDisplayReceptionReason";
import { GetCommercialMonitoringListAPI } from "../api/GetCommercialMonitoringListAPI";
import { useGridPagination } from "../hooks/useGridPagination";
import { HttpStatusCode, isAxiosError } from "axios";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { CustomColumnMenu } from "./CustomColumnMenu";
import { commercialMonitoringDataGridStyles } from "./commercialMonitoringDataGridStyles";
import { formatNumberWithComma } from "@/utils/formatNumber";

const StyledDataGrid = styled(AcquireBookDataGrid)`
  ${commercialMonitoringDataGridStyles}
`;

const makeGridColDef = (): GridColDef[] => {
  return [
    {
      field: "count",
      headerName: "件数",
      width: 80,
      sortable: true,
      align: "right",
      headerAlign: "right",
      renderCell: ({
        value,
      }: GridRenderCellParams<number, CommercialMonitoringSummaryRow>) =>
        formatNumberWithComma(value),
    },
    {
      field: "name",
      headerName: "識別名",
      width: 300,
      sortable: true,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "receptionReasons",
      headerName: "登記原因",
      width: 120,
      sortable: true,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "monthlyUpdatable",
      headerName: "月次更新",
      width: 120,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "legalAffairsBureauRequestDateStart",
      headerName: "法務局受付日 開始",
      width: 160,
      sortable: true,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "legalAffairsBureauRequestDateEnd",
      headerName: "法務局受付日 終了",
      width: 160,
      sortable: true,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "createdAt",
      headerName: "登録日",
      width: 120,
      sortable: true,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 160,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({
        row,
      }: GridRenderCellParams<string, CommercialMonitoringSummaryRow>) => {
        return (
          <Stack direction="row" spacing={2}>
            <LinkButton
              variant={ButtonVariantOption.Contained}
              wrapperSize={SizingWrapperStyle.INHERIT}
              href={`/commercial-monitoring/${row.id}/`}
            >
              詳細
            </LinkButton>
          </Stack>
        );
      },
    },
  ];
};

/**
 * _FeedGrid
 * reload関数を親コンポーネントに外部公開するために、関数名にはアンダースコアをつけている
 * @param _props
 * @param ref
 */
const _FeedGrid: ForwardRefRenderFunction<
  { reload: () => void },
  CommercialMonitoringFeedGridProps
> = (_props, ref) => {
  const { apiClient } = useApiClient();
  const { page, pageSize, changePage, changePageSize, resetPage } =
    useGridPagination();
  const rowsPerPageOptions: number[] = [25, 50, 100];
  const colDef = makeGridColDef();

  const {
    data: apiResponse,
    isLoading,
    mutate,
    error,
  } = GetCommercialMonitoringListAPI(apiClient, _props.filterCondition);

  useEffect(() => {
    _props.setApiResponseData(apiResponse);
    resetPage();
  }, [apiResponse]);

  useEffect(() => {
    if (
      isAxiosError<GetCommercialMonitoringSummariesAPIResponse>(error) &&
      (error.response?.status === HttpStatusCode.BadRequest ||
        error.response?.status === HttpStatusCode.NotFound)
    ) {
      toast.error("指定のデータが存在しません。");
      return;
    }
    if (
      isAxiosError<GetCommercialMonitoringSummariesAPIResponse>(error) &&
      (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
    ) {
      toast.error(
        "データの取得に失敗しました。しばらく経ってから再度お試しください。"
      );
    }
  }, [error]);

  const getDisplayMonthlyUpdatable = (
    monthlyUpdatable: boolean | undefined
  ): DisplayMonthlyUpdatable => {
    return monthlyUpdatable ? "する" : "しない";
  };

  const transferResponseToRows = (
    apiResponse: GetCommercialMonitoringSummariesAPIResponse
  ): CommercialMonitoringSummaryRow[] => {
    const apiResponseData: CommercialMonitoringSummaryRow[] = [];

    for (const summary of apiResponse.summaries) {
      apiResponseData.push({
        id: summary.id,
        count: summary.count,
        name: summary.name,
        receptionReasons: getDisplayReceptionReason(summary.receptionReasons),
        monthlyUpdatable: getDisplayMonthlyUpdatable(summary.monthlyUpdatable),
        legalAffairsBureauRequestDateStart:
          summary.legalAffairsBureauRequestDateStart
            ? format(
                parseISO(summary.legalAffairsBureauRequestDateStart),
                "yyyy/MM/dd"
              )
            : "",
        legalAffairsBureauRequestDateEnd:
          summary.legalAffairsBureauRequestDateEnd
            ? format(
                parseISO(summary.legalAffairsBureauRequestDateEnd),
                "yyyy/MM/dd"
              )
            : "",
        createdAt: format(parseISO(summary.createdAt), "yyyy/MM/dd"),
      });
    }

    return apiResponseData;
  };

  useImperativeHandle(ref, () => {
    return {
      async reload() {
        await mutate();
      },
    };
  });

  if (error != null && apiResponse === undefined) {
    return (
      <Stack alignItems="center">
        <Typography color="error" sx={{ my: "20px" }}>
          データの取得に失敗しました。
        </Typography>
      </Stack>
    );
  }

  if (isLoading || apiResponse === undefined) {
    return (
      <Stack alignItems="center">
        <CircularProgress sx={{ my: "20px" }} />
      </Stack>
    );
  }

  const rows = transferResponseToRows(apiResponse);

  const selectionModelChange = (
    selectionModel: GridSelectionModel,
    _details: GridCallbackDetails
  ): void => {
    _props.setSelectedRowIds(selectionModel);
  };

  const dataGridPaginationProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    onPageChange: changePage,
    onPageSizeChange: changePageSize,
  };

  const toolbarProps: ToolbarProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    changePage,
    changePageSize,
    allCount: rows.length,
  };

  return (
    <StyledDataGrid
      autoHeight
      disableColumnMenu={false}
      disableSelectionOnClick={true}
      checkboxSelection
      rows={rows}
      columns={colDef}
      onSelectionModelChange={selectionModelChange}
      selectionModel={_props.selectedRowIds}
      {...dataGridPaginationProps}
      components={{
        Toolbar: CommercialMonitoringDataGridToolbar,
        ColumnMenu: CustomColumnMenu,
      }}
      componentsProps={{
        toolbar: {
          ...toolbarProps,
        },
      }}
    />
  );
};

/**
 * FeedGrid
 * forwardRefを使ってreload()を外部から呼び出せるようにしている
 */
export const CommercialMonitoringFeedGrid = forwardRef<
  { reload: () => void },
  CommercialMonitoringFeedGridProps
>(_FeedGrid);
